import { isString } from 'radash';
import type { AuthState } from 'types';
import { AuthFlow, ReactNativeMessageType } from 'types';
import { getStoredAuthSession } from 'utils/auth/session';
import { invokeRNMessage } from 'utils/common';
import { STORE_KEYS } from 'types/store';

import { parseRnMessageData } from './parseRnMessageData';

const NATIVE_ACCESS_TOKEN_TIMEOUT_MS = 15_000;

let pendingRequest: Promise<string> | null = null;

const requestNativeAccessToken = (): Promise<string> => {
  if (pendingRequest) {
    return pendingRequest;
  }

  pendingRequest = new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('access_token_timeout'));
    }, NATIVE_ACCESS_TOKEN_TIMEOUT_MS);

    const handleMessage = (event: Event) => {
      const data = parseRnMessageData((event as MessageEvent).data);

      if (data?.type !== ReactNativeMessageType.SendAccessToken) {
        return;
      }

      const { jwtToken } = data.payload;

      if (!isString(jwtToken) || !jwtToken) {
        return;
      }

      cleanup();
      resolve(jwtToken);
    };

    const cleanup = () => {
      window.clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage);
      pendingRequest = null;
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage);
    invokeRNMessage(ReactNativeMessageType.RequestAccessToken, {});
  });

  return pendingRequest;
};

const storeNativeAccessToken = (jwtToken: string) => {
  const stored = getStoredAuthSession();
  const next: AuthState = {
    jwtToken,
    refreshToken: null,
    tokenProvider: stored?.tokenProvider ?? null,
    apiKey: stored?.apiKey ?? process.env.API_KEY ?? '',
    idToken: stored?.idToken ?? null,
    authFlow: stored?.authFlow ?? AuthFlow.Internal,
    keycloakRefreshToken: stored?.keycloakRefreshToken ?? null,
  };

  sessionStorage.setItem(STORE_KEYS.AUTH, JSON.stringify(next));
};

export const recoverMobileKeycloakSession = () => {
  requestNativeAccessToken()
    .then((jwtToken) => {
      storeNativeAccessToken(jwtToken);
      window.location.reload();
    })
    .catch(() => {
      const stored = getStoredAuthSession();

      invokeRNMessage(ReactNativeMessageType.Logout, { jwtToken: stored?.jwtToken ?? null });
    });
};
